import type { CaptureNode } from "./captureClient";

/**
 * A room's capture state, collapsed from its capture node(s) for one board indicator:
 * - `recording` — a node is online and actively capturing.
 * - `online` — a node is online but idle.
 * - `offline` — a node is present but reported offline.
 * - `stale` — the reporting Zuul's link to Brian is down (`connectorLive` false), so the
 *   row may be outdated; shown as unknown, not offline.
 * - `none` — no capture node at all (no indicator).
 */
export type CaptureStatus =
  | "recording"
  | "online"
  | "offline"
  | "stale"
  | "none";

/** Higher wins when a room has several nodes: prefer a definite, positive
 * reading (a node that IS recording) over a stale one whose connector is down. */
const PRIORITY: Record<Exclude<CaptureStatus, "none">, number> = {
  recording: 3,
  online: 2,
  offline: 1,
  stale: 0,
};

/** A single node's status (per-node, before any room-level aggregation). */
export const statusForNode = (
  node: CaptureNode,
): Exclude<CaptureStatus, "none"> => {
  // A down connector means the persisted row may be stale, so its
  // online/recording flags can't be trusted — surface "unknown" first.
  if (!node.connectorLive) return "stale";
  if (node.recording) return "recording";
  if (node.online) return "online";
  return "offline";
};

/**
 * Collapse the capture node(s) mapped to a room into one board status. A room
 * with no node returns `none`; with several, the highest-priority reading wins.
 */
export const captureStatusForRoom = (nodes: CaptureNode[]): CaptureStatus => {
  if (nodes.length === 0) return "none";
  return nodes
    .map(statusForNode)
    .reduce((best, s) => (PRIORITY[s] > PRIORITY[best] ? s : best));
};

/** Group the roster by `roomUuid`; nodes with no room are dropped. */
export const nodesByRoom = (
  nodes: CaptureNode[],
): Map<string, CaptureNode[]> => {
  const byRoom = new Map<string, CaptureNode[]>();
  for (const node of nodes) {
    if (!node.roomUuid) continue;
    const list = byRoom.get(node.roomUuid);
    if (list) list.push(node);
    else byRoom.set(node.roomUuid, [node]);
  }
  return byRoom;
};
